'use client';

import { isDefined } from '@hc-frontend/core-utils-validations';
import { useEffect, useRef, useState } from 'react';
import { IMaskInput } from 'react-imask';

import { FormGroup } from '../../templates';
import { Tooltip } from '../tooltip/tooltip';
import type { InputProps } from './currency-input.types';

function getContainerStyle(withButton: boolean) {
  if (withButton)
    return 'hc-input__container--with-button hc-sidebar-layout--reverse';

  return '';
}

export function CurrencyInput({
  label,
  icon,
  tooltip,
  tooltipText,
  helpText,
  errorText,
  disabled,
  error,
  name,
  id,
  withButton = false,
  button,
  className,
  required,
  onChange,
  initialValue,
  placeholder,
  autoFocus = false,
}: InputProps) {
  const inputRef = useRef<{ element: HTMLInputElement }>(null);
  const showErrors = error;
  const inputId = id || name;

  useEffect(() => {
    if (inputRef.current && autoFocus) {
      inputRef.current.element.focus();
    }
  }, [autoFocus]);

  const [value, setValue] = useState<string | undefined>(initialValue);

  const onCompleteHandler = (newValue: string) => {
    const shouldTriggerOnChange = onChange && isDefined(newValue);

    if (shouldTriggerOnChange) {
      onChange(newValue);
    }

    setValue(newValue);
  };

  const handleContainerClick = () => {
    inputRef.current?.element.focus();
  };

  useEffect(() => {
    if (initialValue) {
      setValue(initialValue);
    }
  }, [initialValue]);

  return (
    <FormGroup className={className} disabled={disabled} error={showErrors}>
      <div className="flex flex-row justify-between">
        <label className="hc-label" id="input-label" htmlFor={inputId}>
          {label}
        </label>
        {tooltipText && (
          <Tooltip
            title={tooltipText}
            className="left-[-270px] top-[90px] w-[290px] bg-primary-background-50 text-text-950 min-[535px]:left-[-280px] min-[535px]:top-[95px] tablet:left-[25px] tablet:top-[-5px] tablet:w-[clamp(50px,25vw,307px)]"
          >
            {tooltip}
          </Tooltip>
        )}
      </div>
      <div className={getContainerStyle(withButton)}>
        <div
          className="hc-form-control relative"
          role="presentation"
          onClick={handleContainerClick}
        >
          {icon}
          <IMaskInput
            ref={inputRef}
            value={value}
            onComplete={onCompleteHandler}
            mask="num"
            lazy={false}
            placeholder={placeholder || 'xxx,xxx'}
            blocks={{
              num: {
                // nested masks are available!
                mask: Number,
                min: 0,
                thousandsSeparator: ',',
              },
            }}
            required={required}
            disabled={disabled}
            name={name}
            id={inputId}
            aria-invalid={showErrors}
            type="tel"
          />
        </div>
        {button}
      </div>
      <div className="hc-help-text">{helpText}</div>
      <div className="hc-error-text">{errorText}</div>
    </FormGroup>
  );
}
